<script>
import {
  iButton,
  iFeaturedIcon,
  iFeaturedIconTypes,
  iFeaturedIconSizes,
  iModal,
  openModal,
  iSwitch,
} from '@inscribe/signal';
import palette from '@inscribe/signal/dist/palette';
import { mapActions, mapMutations } from 'vuex';

export default {
  components: { iButton, iFeaturedIcon, iModal, iSwitch },
  props: {
    signalGroup: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      iFeaturedIconTypes,
      iFeaturedIconSizes,
      palette,
      isSignalGroupEnabled: false,
      reportingData: null,
    };
  },
  mounted() {
    this.isSignalGroupEnabled = this.signalGroup.enabled;
  },
  methods: {
    ...mapActions(['reportDocumentIssue']),
    ...mapMutations([
      'setSelectedInstance',
      'setInstanceEnabled',
      'setDisabledGroup',
    ]),
    onMouseEvent(instance, type) {
      this.setSelectedInstance(type === 'over' ? instance : null);
    },
    onInstanceSwitch(value, instance) {
      this.setInstanceEnabled({ instance, value });
    },
    onRegionClick(instance) {
      this.$router.push(instance.link).catch(() => {});
    },
    onSignalGroupSwitch(value, signalGroup) {
      this.isSignalGroupEnabled = value;
      this.setDisabledGroup({ group: signalGroup, value });
    },
    onReportIssueClicked(instance, pageNumber) {
      this.reportingData = {
        instance,
        pageNumber,
      };
      openModal('report-signal-modal');
    },
    reportIssue() {
      if (this.reportingData) {
        this.reportDocumentIssue({
          customerId: this.signalGroup.customerId,
          documentId: this.signalGroup.documentId,
          issue: {
            signal_name: this.signalGroup.signalKey,
            page: this.reportingData.pageNumber,
            position: this.reportingData.instance.bounding_boxes,
          },
        })
          .then(() => {
            this.$notify({
              group: 'api',
              type: 'success',
              duration: 5000,
              text: `The Inscribe team will investigate the issue`,
            });
            this.problemReported = true;
          })
          .catch(() => {
            this.$notify({
              group: 'api',
              type: 'error',
              title: 'There was a problem reporting your issue',
              duration: 5000,
              text: 'Please try again',
            });
          })
          .finally(() => {
            this.reportingData = null;
          });
      }
    },
  },
};
</script>

<template>
  <div
    data-testid="signal-details"
    class="signal-details d-flex flex-column i-p-10"
  >
    <div class="title d-flex flex-row i-gap-6 i-mb-10 i-mr-12">
      <i-featured-icon
        :size="iFeaturedIconSizes.SM"
        :type="iFeaturedIconTypes.ERROR"
        symbol="small-alert-triangle"
        class="icon"
      ></i-featured-icon>
      <div class="text flex-column align-items-start flex-grow-1">
        <div class="name i-text-button-2 i-text-neutral-120">
          {{ signalGroup.title }}
        </div>
        <div class="subtitle i-text-body-3 i-text-neutral-110">
          {{ signalGroup.subTitle }}
        </div>
      </div>
    </div>
    <div class="d-flex flex-column i-gap-4">
      <!-- Group level signal-->
      <div
        class="group-switch d-flex flex-row i-px-8 i-py-6 i-radius-lg i-mb-10"
      >
        <div class="i-text-subheading-3 i-text-neutral-120">All Regions</div>
        <i-switch
          v-model="isSignalGroupEnabled"
          size="xs"
          @input="(e) => onSignalGroupSwitch(e, signalGroup)"
        />
      </div>

      <!-- signals list -->
      <div
        v-for="(signal, signalIDx) of signalGroup.signals"
        :key="signalIDx"
        class="signal d-flex flex-column i-gap-10"
      >
        <!-- pages list -->
        <div
          v-for="page of signal.pages"
          :key="signalIDx + page.page_number"
          class="page d-flex flex-column"
        >
          <div class="page-divider d-flex flex-row flex-align-center i-mb-8">
            <div class="text i-text-subheading-3 i-text-neutral-110 i-px-4">
              Page {{ page.page_number }}
            </div>
          </div>
          <!-- instances list -->
          <div class="instances-list d-flex flex-column i-gap-4">
            <div
              v-for="(instance, idx) of page.instances"
              :key="instance.id"
              class="instance d-flex flex-row align-items-center i-px-8 i-py-6 i-radius-md"
              @mouseover="onMouseEvent(instance, 'over')"
              @mouseleave="onMouseEvent(instance, 'leave')"
            >
              <div
                class="region d-flex flex-row i-text-subheading-3 i-text-neutral-120"
              >
                <div class="d-flex flex-row align-items-center">
                  Region {{ idx + 1 }}
                </div>
                <div
                  class="controls d-flex flex-row align-items-center i-gap-6"
                >
                  <div class="buttons-container d-flex flex-row i-gap-4">
                    <i-button
                      class="control-button report-button"
                      text="Report"
                      size="xs"
                      type="neutral"
                      subtype="outline"
                      @button-click="
                        onReportIssueClicked(instance, page.page_number)
                      "
                    ></i-button>
                    <i-button
                      class="control-button view-button"
                      text="Jump to"
                      size="xs"
                      type="neutral"
                      :disabled="!isSignalGroupEnabled || !instance.enabled"
                      subtype="outline"
                      @button-click="onRegionClick(instance)"
                    ></i-button>
                  </div>

                  <i-switch
                    v-model="instance.enabled"
                    :disabled="!isSignalGroupEnabled"
                    size="xs"
                    @input="(e) => onInstanceSwitch(e, instance)"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- report Modal -->
        <i-modal
          modal-id="report-signal-modal"
          data-testid="report-signal-modal"
          title="Report an issue with this signal?"
          confirm-button-text="Report"
          type="primary"
          @on-submit="reportIssue"
        >
          <div slot="body-content">
            Reported signals are reviewed to ensure we continuously improve our
            fraud detection accuracy.
          </div>
        </i-modal>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.signal-details {
  .title .icon {
    min-width: 36px;
  }

  .group-switch {
    border: 1px solid $light-neutral-60;
    width: 100%;
    justify-content: space-between;
  }

  .signal {
    .page {
      .page-divider {
        position: relative;
        width: 100%;

        &:after {
          position: absolute;
          content: '';
          background: $light-neutral-60;
          width: 100%;
          height: 1px;
          top: 50%;
          z-index: 0;
          transform: translate(0, -50%);
        }

        .text {
          background: $light-neutral-default;
          z-index: 1;
        }
      }

      .instance {
        background: $light-neutral-20;

        .region {
          width: 100%;
          justify-content: space-between;

          .control-button,
          .i-switch {
            opacity: 0;
            transition: opacity 150ms ease-out;
            pointer-events: none;
          }
        }

        &:hover {
          .control-button,
          .i-switch {
            opacity: 1;
            pointer-events: initial;
          }
        }
      }
    }
  }
}
</style>
